import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../utils/api";
import { cardCls, btnPrimary } from "../ui/styleTokens";
import { MdCheckCircle, MdCheck } from "react-icons/md";
import { FiCalendar, FiShield, FiTag } from "react-icons/fi";

const FEATURE_META = {
  retroplanning: { icon: FiCalendar, bg: "bg-blue-50", text: "text-blue-600" },
  cleaning: { icon: MdCheckCircle, bg: "bg-emerald-50", text: "text-emerald-600" },
  hse: { icon: FiShield, bg: "bg-amber-50", text: "text-amber-600" },
  badging: { icon: FiTag, bg: "bg-purple-50", text: "text-purple-600" },
};
const DEFAULT_META = { icon: FiTag, bg: "bg-gray-100", text: "text-gray-500" };

const inputCls = "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition disabled:bg-gray-50 disabled:text-gray-400";
const labelCls = "block text-xs font-semibold text-gray-700 mb-1.5";

const emptyClient = { clientName: "", clientEmail: "", clientPhone: "", clientAddress: "", clientIce: "", clientRc: "" };

const CreateInvoiceTab = () => {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [features, setFeatures] = useState([]);
  const [selected, setSelected] = useState([]);
  const [client, setClient] = useState(emptyClient);
  const [remisePercent, setRemisePercent] = useState(0);
  const [template, setTemplate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null);

  useEffect(() => {
    axiosInstance.get("/event")
      .then((r) => setEvents(r.data?.data || []))
      .catch((err) => console.error("Failed to load events", err));
    axiosInstance.get("/billing/pricing")
      .then((r) => setFeatures(r.data || []))
      .catch((err) => console.error("Failed to load pricing", err));
    axiosInstance.get("/billing/template")
      .then((r) => setTemplate(r.data))
      .catch((err) => console.error("Failed to load invoice template", err));
  }, []);

  const toggleFeature = (key) =>
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const subtotal = useMemo(
    () => features.filter((f) => selected.includes(f.key)).reduce((sum, f) => sum + f.price, 0),
    [features, selected]
  );
  const discountAmount = (subtotal * (remisePercent || 0)) / 100;
  const totalHT = subtotal - discountAmount;

  const showTva = template?.showTva ?? true;
  const tvaRate = template?.tvaRate ?? 20;
  const tvaAmount = showTva ? totalHT * (tvaRate / 100) : 0;
  const totalTTC = totalHT + tvaAmount;

  const resetForm = () => {
    setClient(emptyClient);
    setSelected([]);
    setRemisePercent(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setCreated(null);
    if (!eventId) return setError("Select an event first");
    if (!client.clientName.trim()) return setError("Client name is required");
    if (selected.length === 0) return setError("Select at least one feature");

    setSubmitting(true);
    try {
      const res = await axiosInstance.post("/billing/invoices", {
        event: eventId,
        ...client,
        featureKeys: selected,
        remisePercent: Number(remisePercent) || 0,
      });
      setCreated(res.data);
      resetForm();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to create invoice");
    } finally {
      setSubmitting(false);
    }
  };

  const formDisabled = !eventId;

  return (
    <div className={`${cardCls} p-5`}>
      <div className="flex items-baseline justify-between mb-1">
        <h4 className="font-semibold text-gray-900 text-sm">Create invoice</h4>
        {created && (
          <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
            <MdCheckCircle size={14} /> {created.invoiceNumber} created
          </span>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-5">
        Pick the event this invoice belongs to, then fill in the client and the features used.
      </p>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="max-w-xs">
          <label className={labelCls}>Event *</label>
          <select value={eventId} onChange={(e) => setEventId(e.target.value)} className={inputCls}>
            <option value="">Select an event…</option>
            {events.map((ev) => (
              <option key={ev._id} value={ev._id}>{ev.name}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Client name *</label>
            <input disabled={formDisabled} value={client.clientName} onChange={(e) => setClient({ ...client, clientName: e.target.value })} className={inputCls} placeholder="Company or full name" />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input disabled={formDisabled} value={client.clientEmail} onChange={(e) => setClient({ ...client, clientEmail: e.target.value })} className={inputCls} placeholder="client@example.com" />
          </div>
          <div>
            <label className={labelCls}>Phone</label>
            <input disabled={formDisabled} value={client.clientPhone} onChange={(e) => setClient({ ...client, clientPhone: e.target.value })} className={inputCls} placeholder="Optional" />
          </div>
          <div>
            <label className={labelCls}>Address</label>
            <input disabled={formDisabled} value={client.clientAddress} onChange={(e) => setClient({ ...client, clientAddress: e.target.value })} className={inputCls} placeholder="Optional" />
          </div>
          {template?.showClientIce && (
            <div>
              <label className={labelCls}>Client ICE</label>
              <input disabled={formDisabled} value={client.clientIce} onChange={(e) => setClient({ ...client, clientIce: e.target.value })} className={inputCls} placeholder="00XXXXXXXXXXXXX" />
            </div>
          )}
          {template?.showClientRc && (
            <div>
              <label className={labelCls}>Client RC</label>
              <input disabled={formDisabled} value={client.clientRc} onChange={(e) => setClient({ ...client, clientRc: e.target.value })} className={inputCls} placeholder="Optional" />
            </div>
          )}
        </div>

        <div>
          <label className={labelCls}>Features used</label>
          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${formDisabled ? "opacity-50 pointer-events-none" : ""}`}>
            {features.map((f) => {
              const meta = FEATURE_META[f.key] || DEFAULT_META;
              const Icon = meta.icon;
              const active = selected.includes(f.key);
              return (
                <button
                  type="button"
                  key={f.key}
                  onClick={() => toggleFeature(f.key)}
                  className={`relative text-left border rounded-xl p-4 transition-colors ${active ? "border-blue-600 bg-blue-50/40" : "border-gray-200 bg-white hover:border-gray-300"}`}
                >
                  {active && (
                    <span className="absolute top-3 right-3 w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center">
                      <MdCheck size={13} />
                    </span>
                  )}
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${meta.bg} ${meta.text}`}>
                      <Icon size={18} />
                    </div>
                    <p className="text-sm font-semibold text-gray-900">{f.name}</p>
                  </div>
                  {f.description && (
                    <p className="text-xs text-gray-500 leading-relaxed mb-2 line-clamp-2">{f.description}</p>
                  )}
                  <p className="text-sm font-medium text-gray-700 tabular-nums">{f.price} MAD</p>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between max-w-xs">
          <label className={labelCls}>Remise (%)</label>
          <input disabled={formDisabled} type="number" min="0" max="100" value={remisePercent} onChange={(e) => setRemisePercent(e.target.value)} className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-sm text-right disabled:bg-gray-50" />
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 space-y-1.5 max-w-sm">
          <div className="flex justify-between text-sm text-gray-600">
            <span>Subtotal HT</span><span className="tabular-nums">{subtotal.toFixed(2)} MAD</span>
          </div>
          {remisePercent > 0 && (
            <div className="flex justify-between text-sm text-red-600">
              <span>Remise ({remisePercent}%)</span><span className="tabular-nums">-{discountAmount.toFixed(2)} MAD</span>
            </div>
          )}
          <div className="flex justify-between text-sm text-gray-600">
            <span>Total HT</span><span className="tabular-nums">{totalHT.toFixed(2)} MAD</span>
          </div>
          {showTva && (
            <div className="flex justify-between text-sm text-gray-600">
              <span>TVA ({tvaRate}%)</span><span className="tabular-nums">{tvaAmount.toFixed(2)} MAD</span>
            </div>
          )}
          <div className="flex justify-between text-sm font-semibold text-gray-900 pt-1.5 border-t border-gray-200">
            <span>Total TTC</span><span className="tabular-nums">{totalTTC.toFixed(2)} MAD</span>
          </div>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end">
          <button type="submit" disabled={submitting || formDisabled} className={btnPrimary}>
            {submitting ? "Creating…" : "Create invoice"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateInvoiceTab;